import type { CSSProperties } from "react";
import type { PmBranding } from "@/api/PmConsoleApi";

export type Product = "bondoq" | "rentinit";

// Capabilities identify the product, never an agency's logo or palette. The
// route fallback also gives loading/error screens and older kernels an identity.
export function consoleProduct(
  branding?: PmBranding,
  agency?: string,
): Product {
  if (branding?.console?.leadsai) return "bondoq";
  if (branding?.console?.residentai) return "rentinit";
  return agency && agency !== "rentinit" && agency !== "residentai"
    ? "bondoq"
    : "rentinit";
}

const URBANIST =
  "'Urbanist',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif";
const APP_TEAL = "#0AAAC4";
export const RENTINIT_VARS: Record<string, string> = {
  "--brand": APP_TEAL,
  "--on-brand": "#0A0F1A",
  "--tint": `color-mix(in oklab, ${APP_TEAL} 9%, transparent)`,
  "--soft": `color-mix(in oklab, ${APP_TEAL} 26%, transparent)`,
  "--ink-strong": "#087A8D",
  "--accent": APP_TEAL,
  "--accent-tint": `color-mix(in oklab, ${APP_TEAL} 12%, transparent)`,
  "--accent-soft": `color-mix(in oklab, ${APP_TEAL} 45%, transparent)`,
  // Darker step of the brand teal — accent-ink is TEXT (message links,
  // direction chips) and the brand tone itself is too light on white.
  "--accent-ink": "#087A8D",
  "--bg": "#F3F6F9",
  "--panel": "#FFFFFF",
  "--bar": "#FFFFFF",
  "--border": "#E2EAF0",
  "--text": "#172D3D",
  "--muted-s": "#475F70",
  "--muted": "#617584",
  "--muted-x": "#718592",
  "--field": "#F8FAFC",
  "--seg-bg": "var(--action)",
  "--seg-ink": "var(--action-foreground)",
  "--pm-font": URBANIST,
  "--signal": "#B26B32",
  "--signal-tint": "rgba(178,107,50,0.10)",
  "--signal-soft": "rgba(178,107,50,0.28)",
};

// One console design system, with fixed product accents. Agency settings are
// business context and cannot change the identity of the software.
export const BONDOQ_VARS = {
  ...RENTINIT_VARS,
  "--brand": "#1A9798",
  "--on-brand": "#071D21",
  "--tint": "#1A979817",
  "--soft": "#1A979842",
  "--ink-strong": "#117475",
  "--accent": "#1A9798",
  "--accent-tint": "#1A97981F",
  "--accent-soft": "#1A979873",
  "--accent-ink": "#117475",
} as CSSProperties;

export function productTheme(product: Product): CSSProperties {
  return product === "bondoq" ? BONDOQ_VARS : (RENTINIT_VARS as CSSProperties);
}
